/**
 * Interview-buffer concern for the Details step. Owns:
 *   - the account-level buffer setting fetch (the master switch +
 *     self-schedule-adhoc seed source),
 *   - one-shot hydration of the buffer fields into shared state (adhoc),
 *   - the seed-gate reset when leaving the adhoc context,
 *   - the `handleBufferChange` callback.
 *
 * Returns `accountBufferEnabled` so `DetailsStep` can gate the buffer
 * control's visibility on the account-level setting (account OFF hides
 * the per-interview control everywhere, incl. edit — account is the
 * master switch). `bufferFlagOn` (the FE rollout flag for the mode) is
 * computed in `DetailsStep` and threaded in.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useAccountBuffer } from '../../../api/useAccountBuffer';
import {
  DEFAULT_BUFFER_BEFORE_MINUTES,
  DEFAULT_BUFFER_AFTER_MINUTES,
  coerceBufferMinutes,
} from '../../../calendar/selfSchedule/buffer';
import type { ScheduleSharedState } from '../../../state/useScheduleSharedState';

interface UseDetailsBufferArgs {
  bufferFlagOn: boolean;
  apiReference?: string;
  interviewPlan?: Record<string, unknown>;
  updateSharedState: (_partial: Partial<ScheduleSharedState>) => void;
}

interface UseDetailsBufferResult {
  handleBufferChange: (_next: {
    before: number;
    after: number;
    enabled: boolean;
  }) => void;
  /** Account-level (BE `effective_buffer_enabled?`, LD-gated) flag. */
  accountBufferEnabled: boolean;
}

export function useDetailsBuffer({
  bufferFlagOn,
  apiReference,
  interviewPlan,
  updateSharedState,
}: UseDetailsBufferArgs): UseDetailsBufferResult {
  // Account-level buffer setting (`/ats_schedules/settings`). Needed for
  // the master-switch gate whenever we can't read it synchronously off
  // the plan: edit (where `interviewPlan` is the saved interview, not the
  // account default) and self-schedule-adhoc (no plan). Create-with-plan
  // reads the account-effective flag straight off the plan response, so
  // it doesn't need this fetch.
  const isAdhoc = !interviewPlan;
  const { data: accountBuffer } = useAccountBuffer({
    enabled: bufferFlagOn && (apiReference === 'edit' || isAdhoc),
  });
  const accountBufferEnabled =
    accountBuffer?.interviewer_buffer_enabled === true;

  // Hydrate buffer fields into sharedState once when the
  // self-schedule-adhoc account fetch resolves. Guards:
  //   - run only when the fallback is the active source (no plan).
  //   - run only once via the seen-id ref; user edits then take over.
  const adhocBufferSeededRef = useRef(false);
  useEffect(() => {
    if (!bufferFlagOn || interviewPlan) return;
    if (adhocBufferSeededRef.current) return;
    if (!accountBuffer) return;
    adhocBufferSeededRef.current = true;
    const updates: Partial<ScheduleSharedState> = {};
    const pre = coerceBufferMinutes(
      accountBuffer.interviewer_buffer_pre_minutes
    );
    if (pre !== undefined) updates.bufferBeforeMinutes = pre;
    const post = coerceBufferMinutes(
      accountBuffer.interviewer_buffer_post_minutes
    );
    if (post !== undefined) updates.bufferAfterMinutes = post;
    if (typeof accountBuffer.interviewer_buffer_enabled === 'boolean')
      updates.bufferEnabled = accountBuffer.interviewer_buffer_enabled;
    if (Object.keys(updates).length > 0) updateSharedState(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bufferFlagOn, interviewPlan, accountBuffer]);

  // Reset the seed gate when leaving self-schedule-adhoc context so a
  // later return to it can re-hydrate from the fresh account response.
  if (!bufferFlagOn || interviewPlan) {
    adhocBufferSeededRef.current = false;
  }

  const handleBufferChange = useCallback(
    (next: { before: number; after: number; enabled: boolean }) => {
      // Toggle-on with 0/0 → auto-seed sensible defaults so the
      // pill doesn't read "Buffer: 0m before + 0m after" (cosmetic
      // regression for fresh accounts where BE returns 0/0/false
      // and the user just flipped the eye-on without editing).
      const before =
        next.enabled && next.before === 0 && next.after === 0
          ? DEFAULT_BUFFER_BEFORE_MINUTES
          : next.before;
      const after =
        next.enabled && next.before === 0 && next.after === 0
          ? DEFAULT_BUFFER_AFTER_MINUTES
          : next.after;
      updateSharedState({
        bufferBeforeMinutes: before,
        bufferAfterMinutes: after,
        bufferEnabled: next.enabled,
      });
    },
    [updateSharedState]
  );

  return { handleBufferChange, accountBufferEnabled };
}
